import { USAGE_DEFAULT_SIZE_MAP as sizeMap } from '@/utils';
import { API_BASE_URI } from '@/store';
import { ParsedFrame } from './AnimationTypes';

export const buildSingleFrameUrl = (
    subDir: string,
    frameStem: string,
    targetSize: string,
): string => {
    const styleMachineName = sizeMap[targetSize] || sizeMap['m'];
    const baseUri = API_BASE_URI || window.location.origin;
    // Cleans out accidental edge trailing slash occurrences gracefully
    const sanitizedBase = baseUri.endsWith('/')
        ? baseUri.slice(0, -1)
        : baseUri;
    return `${sanitizedBase}/files/styles/${styleMachineName}/public/animations/${subDir}/${frameStem}.webp`;
};

export const getFramesArray = (alignmentDataJson: string): ParsedFrame[] => {
    if (!alignmentDataJson) return [];
    try {
        return Object.entries(JSON.parse(alignmentDataJson)).map(
            ([frameStem, values]) => {
                const rawAlignments = Array.isArray(values[0])
                    ? (values as number[][])
                    : [values as number[]];

                const alignments = rawAlignments.map((arr) => ({
                    offsetX: arr[0] ?? 0,
                    offsetY: arr[1] ?? 0,
                    scale: arr[2] ?? 1,
                    roll: arr[3] ?? 0,
                    pitch: arr[4] ?? 0,
                    yaw: arr[5] ?? 0,
                }));

                return {
                    frameStem,
                    alignments,
                };
            },
        );
    } catch (e) {
        console.error('Failed parsing animation alignment JSON payload:', e);
        return [];
    }
};

// src/components/media/Animation/AnimationUtilities.ts

export interface SpacingExtremes {
    maxAbsX: number; // Furthest left/right coordinate reach from center
    maxAbsY: number; // Furthest top/bottom coordinate reach from center
}

export interface PerfectCanvasDimensions {
    width: number;
    height: number;
}

/**
 * Iterates through the entire alignment timeline array to track spatial boundary extremes.
 * Accounts for simultaneous translation offsets, scale, roll, pitch and yaw.
 */
export const calculatePerfectCanvasDimensions = (
    frames: ParsedFrame[],
    baseWidth: number,
    baseHeight: number,
    alignmentIndex: number = 0,
): PerfectCanvasDimensions => {
    if (!frames.length || !baseWidth || !baseHeight) {
        return { width: baseWidth || 1200, height: baseHeight || 900 };
    }

    const halfW = baseWidth / 2;
    const halfH = baseHeight / 2;

    const baseCorners = [
        { x: -halfW, y: -halfH },
        { x: halfW, y: -halfH },
        { x: halfW, y: halfH },
        { x: -halfW, y: halfH },
    ];

    const frameWidths: number[] = [];
    const frameHeights: number[] = [];

    frames.forEach((frame) => {
        const align = frame.alignments[alignmentIndex] ||
            frame.alignments[0] || {
                offsetX: 0,
                offsetY: 0,
                scale: 1,
                roll: 0,
            };

        const safeScale = Math.min(2.5, Math.max(0.2, align.scale ?? 1));

        // Convert percentage offsets to pixels relative to base dimension
        const pixelOffsetX = baseWidth * ((align.offsetX ?? 0) / 100.0);
        const pixelOffsetY = baseHeight * ((align.offsetY ?? 0) / 100.0);
        const roll = align.roll ?? 0;

        const radians = (roll * Math.PI) / 180;
        const cosR = Math.cos(radians);
        const sinR = Math.sin(radians);

        let maxAbsX = halfW;
        let maxAbsY = halfH;

        baseCorners.forEach((corner) => {
            const sx = corner.x * safeScale;
            const sy = corner.y * safeScale;

            const rx = sx * cosR - sy * sinR;
            const ry = sx * sinR + sy * cosR;

            const finalX = Math.abs(rx + pixelOffsetX);
            const finalY = Math.abs(ry + pixelOffsetY);

            if (finalX > maxAbsX) maxAbsX = finalX;
            if (finalY > maxAbsY) maxAbsY = finalY;
        });

        frameWidths.push(maxAbsX * 2);
        frameHeights.push(maxAbsY * 2);
    });

    frameWidths.sort((a, b) => a - b);
    frameHeights.sort((a, b) => a - b);

    const mid = Math.floor(frameWidths.length / 2);
    const medianWidth =
        frameWidths.length % 2 !== 0
            ? frameWidths[mid]
            : (frameWidths[mid - 1] + frameWidths[mid]) / 2;

    const medianHeight =
        frameHeights.length % 2 !== 0
            ? frameHeights[mid]
            : (frameHeights[mid - 1] + frameHeights[mid]) / 2;

    const BUFFER_FACTOR = 1.075;

    const finalWidth = Math.ceil(medianWidth * BUFFER_FACTOR);
    const finalHeight = Math.ceil(medianHeight * BUFFER_FACTOR);

    return {
        width: finalWidth,
        height: finalHeight,
    };
};

/**
 * Derives the exact percentage scaling limits required for the placeholder still image overlay.
 */
export const calculateStillScaleStyle = (
    frames: ParsedFrame[],
    baseWidth: number,
    baseHeight: number,
) => {
    if (!frames.length || !baseWidth || !baseHeight)
        return { maxWidth: '100%', maxHeight: '100%' };

    const perfectDim = calculatePerfectCanvasDimensions(
        frames,
        baseWidth,
        baseHeight,
    );

    // Calculate how much the base asset needs to scale down relative to the full canvas boundary
    const widthRatio = (baseWidth / perfectDim.width) * 100;
    const heightRatio = (baseHeight / perfectDim.height) * 100;

    return {
        maxWidth: `${widthRatio.toFixed(4)}%`,
        maxHeight: `${heightRatio.toFixed(4)}%`,
    };
};
